import { z } from 'zod';

import {
  Company,
  ModuleSettingsItem,
  User,
  VisibilityComparator,
  VisibilityRule,
  VisibilityRuleType,
} from 'src/constants';
import { Tab } from 'src/types/crm_details';
import { ensureUnreachable } from 'src/utils/common_utils';

export enum VisibilityType {
  VisibleToEveryone = 'visibleToEveryone',
  NoVisibility = 'noVisibility',
  CustomVisibility = 'customVisibility',
}

export type AllVisibilityRules =
  | { visibility: VisibilityType.VisibleToEveryone; visibilityConfig: [] }
  | {
      visibility: VisibilityType.NoVisibility;
      visibilityConfig: [];
    }
  | {
      visibility: VisibilityType.CustomVisibility;
      visibilityConfig: VisibilityRule[];
    };

export enum VisibilityScenario {
  EditApp = 'EditApp',
  AddAnApp = 'AddAnApp',
}

export const clientRuleSchema = z.object({
  ruleType: z.literal(VisibilityRuleType.Client),
  ruleKey: z.string(),
  comparator: z.enum([
    VisibilityComparator.IncludesAnyOf,
    VisibilityComparator.DoesntContainAnyOf,
    VisibilityComparator.IncludesAllOf,
  ]),
  values: z.array(z.string()).min(1, { message: 'Select at least one value' }),
});

export const companyRuleSchema = z.object({
  ruleType: z.literal(VisibilityRuleType.Company),
  ruleKey: z.string(),
  comparator: z.enum([
    VisibilityComparator.IncludesAnyOf,
    VisibilityComparator.DoesntContainAnyOf,
    VisibilityComparator.IncludesAllOf,
  ]),
  values: z.array(z.string()).min(1, { message: 'Select at least one value' }),
});

export const customFieldRuleSchema = z.object({
  ruleType: z.literal(VisibilityRuleType.CustomField),
  ruleKey: z.string(),
  comparator: z.enum([
    VisibilityComparator.IncludesAnyOf,
    VisibilityComparator.DoesntContainAnyOf,
    VisibilityComparator.IncludesAllOf,
  ]),
  values: z.array(z.string()).min(1, { message: 'Select at least one value' }),
  customFieldId: z.string(),
  isCompanyCustomField: z.boolean().optional(),
});

type CustomFieldRuleSchema = z.infer<typeof customFieldRuleSchema>;

export type VisibilityRuleSchema = z.infer<typeof rulesSchema>;

const visbileToEveryOneSchema = z.object({
  visibility: z.literal(VisibilityType.VisibleToEveryone),
});

const noVisibilitySchema = z.object({
  visibility: z.literal(VisibilityType.NoVisibility),
});

const customVisibilitySchema = z.object({
  visibility: z.literal(VisibilityType.CustomVisibility),
  visibilityConfig: z.array(
    z.discriminatedUnion('ruleType', [
      clientRuleSchema,
      companyRuleSchema,
      customFieldRuleSchema,
    ]),
  ),
});

export type VisibileToEveryoneSchema = z.infer<typeof visbileToEveryOneSchema>;
export type CustomVisibilitySchema = z.infer<typeof customVisibilitySchema>;
export type NoVisibilitySchema = z.infer<typeof noVisibilitySchema>;

export const rulesSchema = z.discriminatedUnion('visibility', [
  visbileToEveryOneSchema,
  customVisibilitySchema,
  noVisibilitySchema,
]);

export function isCustomFieldUsedInAppVisibility(
  moduleSettings: ModuleSettingsItem[],
  deletedOption: string,
  deletionComparison: (
    rule: VisibilityRule,
    deletedCustomField: string,
  ) => boolean,
): [boolean, string] {
  for (const module of moduleSettings) {
    const { visibilityConfig } = module;
    if (
      visibilityConfig &&
      visibilityConfig.length > 0 &&
      visibilityConfig.some((visibilityRule) =>
        deletionComparison(visibilityRule, deletedOption),
      )
    ) {
      return [true, module.label];
    }
  }
  return [false, ''];
}

export function getVisibilityConfig(
  values: AllVisibilityRules,
): VisibilityRule[] {
  const { visibility } = values;
  switch (visibility) {
    case VisibilityType.VisibleToEveryone:
      return [];
    case VisibilityType.NoVisibility:
      return [{ ruleType: VisibilityRuleType.NoVisibility }];
    case VisibilityType.CustomVisibility:
      return values.visibilityConfig.map((rule) => {
        if (isCustomFieldRule(rule)) {
          return { ...rule, customFieldId: rule.customFieldId };
        }
        return rule;
      });
    default:
      return ensureUnreachable(visibility);
  }
}

export function isCustomFieldRule(
  rule: AllVisibilityRules['visibilityConfig'][0],
): rule is CustomFieldRuleSchema {
  return (
    rule.ruleType === VisibilityRuleType.CustomField && 'customFieldId' in rule
  );
}

export function isVisibilityType(value: any): value is VisibilityType {
  return Object.values(VisibilityType).includes(value);
}

export function getVisibilityType(moduleVisbilityConfig: VisibilityRule[]) {
  if (moduleVisbilityConfig.length === 0) {
    return VisibilityType.VisibleToEveryone;
  }
  if (
    moduleVisbilityConfig.length === 1 &&
    moduleVisbilityConfig.at(0)?.ruleType === VisibilityRuleType.NoVisibility
  ) {
    return VisibilityType.NoVisibility;
  }
  return VisibilityType.CustomVisibility;
}

export function getClientVisibilityCount(
  scenario: VisibilityScenario,
  clients: User[],
  companies: Company[],
  visibilityConfig: VisibilityRule[],
  serverClientCount: number,
) {
  let count = 0;
  switch (scenario) {
    case VisibilityScenario.AddAnApp: {
      // Create a map of companies by ID for efficient lookup
      const companiesMap = new Map<string, Company>();
      for (const company of companies) {
        companiesMap.set(company.id, company);
      }

      for (const client of clients) {
        if (
          hasClientVisibilityIntoModule(visibilityConfig, client, companiesMap)
        ) {
          count += 1;
        }
      }
      return count;
    }
    case VisibilityScenario.EditApp:
      return serverClientCount;
    default:
      ensureUnreachable(scenario);
  }
}

export function hasClientVisibilityIntoModule(
  visibilityConfig: VisibilityRule[],
  client: User,
  companiesMap?: Map<string, Company>,
): boolean {
  if (visibilityConfig.length === 0) {
    return true;
  }

  for (const rule of visibilityConfig) {
    const { ruleType } = rule;
    switch (ruleType) {
      case VisibilityRuleType.Client: {
        const { comparator, values } = rule;
        switch (comparator) {
          case VisibilityComparator.IncludesAnyOf:
            if (values.includes(client.id)) {
              return true;
            }
            break;
          case VisibilityComparator.DoesntContainAnyOf:
          case VisibilityComparator.IncludesAllOf:
            continue;
          default:
            ensureUnreachable(comparator);
        }
        continue;
      }
      case VisibilityRuleType.Company: {
        const { comparator, values } = rule;
        switch (comparator) {
          case VisibilityComparator.IncludesAnyOf:
            if (
              // TODO:(enable_many_companies) remove the companyId check once we enable many companies
              values.includes(client.fields.companyId || '') ||
              client.fields.companyIds?.some((id) => values.includes(id))
            ) {
              return true;
            }
            break;
          case VisibilityComparator.DoesntContainAnyOf:
          case VisibilityComparator.IncludesAllOf:
            continue;
          default:
            ensureUnreachable(comparator);
        }
        continue;
      }
      case VisibilityRuleType.CustomField: {
        const { comparator, values, customFieldId, isCompanyCustomField } =
          rule;

        // Handle company custom fields
        if (isCompanyCustomField) {
          if (!companiesMap) {
            // No companies map provided, skip this rule
            continue;
          }

          // Get client's company IDs
          const clientCompanyIds: string[] = client.fields.companyIds || [];

          // Check each company's custom fields
          let convertedValueForCustomFields: string[] | null = null;

          for (const cId of clientCompanyIds) {
            const company = companiesMap.get(cId);
            if (company) {
              const companyCustomFieldValue =
                company.fields.customFields?.[customFieldId];
              if (
                companyCustomFieldValue &&
                Array.isArray(companyCustomFieldValue)
              ) {
                convertedValueForCustomFields = companyCustomFieldValue;
                break; // Found matching company, exit loop
              }
            }
          }

          if (convertedValueForCustomFields !== null) {
            const customFieldIds = new Set(convertedValueForCustomFields);
            switch (comparator) {
              case VisibilityComparator.IncludesAnyOf:
                if (values.some((value) => customFieldIds.has(value))) {
                  return true;
                }
                break;
              case VisibilityComparator.IncludesAllOf:
                if (values.every((value) => customFieldIds.has(value))) {
                  return true;
                }
                break;
              case VisibilityComparator.DoesntContainAnyOf:
                if (!values.some((value) => customFieldIds.has(value))) {
                  return true;
                }
                break;
              default:
                ensureUnreachable(comparator);
            }
          } else {
            // Custom field not found - similar to backend behavior
            switch (comparator) {
              case VisibilityComparator.DoesntContainAnyOf:
                // If field doesn't exist, it doesn't contain any of the values
                return true;
              default:
                // For other comparators, continue to next rule
                break;
            }
          }
        } else {
          // Client custom fields (existing logic)
          switch (comparator) {
            case VisibilityComparator.IncludesAnyOf: {
              const customFieldValue = new Set(
                client.fields.customFields?.[customFieldId],
              );
              if (values.some((value) => customFieldValue.has(value))) {
                return true;
              }
              break;
            }
            case VisibilityComparator.DoesntContainAnyOf: {
              const customFieldValue = new Set(
                client.fields.customFields?.[customFieldId],
              );
              if (!values.some((value) => customFieldValue.has(value))) {
                return true;
              }
              break;
            }
            case VisibilityComparator.IncludesAllOf:
              {
                const customFieldValue = new Set(
                  client.fields.customFields?.[customFieldId],
                );
                if (values.every((value) => customFieldValue.has(value))) {
                  return true;
                }
              }
              continue;
            default:
              ensureUnreachable(comparator);
          }
        }
        continue;
      }
      case VisibilityRuleType.NoVisibility: {
        continue;
      }

      default:
        ensureUnreachable(ruleType);
    }
  }

  return false;
}

export function isTabVisibleToClient(
  tab: Tab<string>,
  activeClient: User,
  visibilityConfigMapping: Record<string, VisibilityRule[]>,
) {
  switch (tab.key) {
    case 'contracts':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['contracts'],
        activeClient,
      );
    case 'files':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['files'],
        activeClient,
      );
    case 'forms':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['forms'],
        activeClient,
      );
    case 'messages':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['messages'],
        activeClient,
      );
    case 'invoices':
      return hasClientVisibilityIntoModule(
        visibilityConfigMapping['payments'],
        activeClient,
      );
    default:
      if (z.string().uuid().safeParse(tab.key).success) {
        return hasClientVisibilityIntoModule(
          visibilityConfigMapping[tab.key],
          activeClient,
        );
      } else {
        return false;
      }
  }
}
